import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, useToast } from '@kss/ui';
import { Link } from 'react-router-dom';
import {
  restaurantOrderService,
  restaurantTableService,
  ORDER_STATUSES,
  CUSTOMER_VISIBLE_STEPS,
  isActiveOrder,
  statusColor,
  type RestaurantOrder,
  type RestaurantOrderStatus,
  type RestaurantTable,
} from '@kss/api';
import { RefreshCw, ChefHat, Users, ClipboardList } from 'lucide-react';

/**
 * Table-wise live-order board for floor managers.
 *
 *  - One card per restaurant table.
 *  - Each card lists the table's active orders (Placed / Accepted /
 *    Cooking / Ready) with a colored status pill.
 *  - Free tables show a green "Available" badge.
 *  - Tables with any kitchen-pending order get a flashing accent.
 *
 * Polls every 10s. Sibling to the Kitchen Display (which is the
 * per-ticket pipeline view).
 */

interface BoardOrder {
  id: string;
  code: string;
  status: RestaurantOrderStatus;
  customerName?: string;
  placedAt?: string;
  items?: Array<{ name: string; quantity: number }>;
  tableId?: string;
}

interface BoardTable {
  tableId: string;
  code: string;
  name?: string;
  capacity?: number;
  zone?: string;
  orders: BoardOrder[];
}

const POLL_MS = 10_000;

export default function TableStatusBoard() {
  const toast = useToast();
  const [tables, setTables] = useState<BoardTable[]>([]);
  const [unassigned, setUnassigned] = useState<BoardOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [tableRows, orderRows] = await Promise.all([
        restaurantTableService.all(),
        restaurantOrderService.all(),
      ]);
      const orders = orderRows.map((r) => parseOrder(r as RestaurantOrder));
      const open = orders.filter((o) => isActiveOrder(o.status));

      const byTable = new Map<string, BoardOrder[]>();
      const orphan: BoardOrder[] = [];
      for (const o of open) {
        if (o.tableId && o.tableId !== 'undefined' && o.tableId !== 'null') {
          if (!byTable.has(o.tableId)) byTable.set(o.tableId, []);
          byTable.get(o.tableId)!.push(o);
        } else {
          orphan.push(o);
        }
      }

      const out: BoardTable[] = (tableRows as RestaurantTable[])
        .filter((t) => t.isActive !== false)
        .map((t) => ({
          tableId: String(t.restaurantTableId ?? ''),
          code: t.code ?? t.name ?? '—',
          name: t.name,
          capacity: t.capacity,
          zone: (t as { zone?: string }).zone,
          orders: byTable.get(String(t.restaurantTableId ?? '')) ?? [],
        }))
        .sort((a, b) => a.code.localeCompare(b.code));

      setTables(out);
      setUnassigned(orphan);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load table board.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const t = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(t);
  }, [refresh]);

  const counts = useMemo(() => {
    let occupied = 0;
    let cooking = 0;
    let ready = 0;
    for (const t of tables) {
      if (t.orders.length === 0) continue;
      occupied += 1;
      if (t.orders.some((o) => o.status === 'Cooking')) cooking += 1;
      if (t.orders.some((o) => o.status === 'Ready')) ready += 1;
    }
    return { occupied, free: tables.length - occupied, cooking, ready };
  }, [tables]);

  return (
    <div className="-m-6 flex h-[calc(100vh-3.5rem)] flex-col">
      <header className="flex items-center gap-3 border-b border-border bg-surface-card px-4 py-3">
        <ClipboardList className="text-orange-500" size={22} />
        <div className="flex-1">
          <h1 className="text-base font-semibold text-content-primary">Tables — Live Orders</h1>
          <p className="text-[11px] text-content-muted">
            {tables.length} tables · {counts.occupied} occupied · {counts.cooking} cooking · {counts.ready} ready
            {unassigned.length > 0 ? ` · ${unassigned.length} unassigned tickets` : ''}
          </p>
        </div>
        <Link to="/panel/kitchen" className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-surface-card px-3 text-xs font-medium text-content-primary hover:bg-surface-card-hover">
          <ChefHat size={13} />
          Open Kitchen Display
        </Link>
        <Button variant="outline" size="sm" onClick={() => void refresh()}>
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </header>

      {err && (
        <Alert variant="danger" className="m-3">
          {err}
        </Alert>
      )}

      <div className="flex-1 overflow-y-auto p-3">
        {loading && tables.length === 0 ? (
          <div className="grid place-items-center py-20 text-sm text-content-muted">Loading tables…</div>
        ) : tables.length === 0 ? (
          <div className="grid place-items-center py-20 text-center text-sm text-content-muted">
            No tables configured. Add some in <Link to="/panel/restaurant-tables" className="text-orange-500 underline">Restaurant Tables</Link>.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {tables.map((t) => (
              <TableCard key={t.tableId} table={t} />
            ))}
          </div>
        )}

        {unassigned.length > 0 && (
          <div className="mt-6">
            <h2 className="mb-2 text-sm font-semibold text-content-primary">
              Unassigned tickets ({unassigned.length})
            </h2>
            <p className="mb-2 text-xs text-content-muted">
              These orders don’t carry a `tableId` — typically POS / takeaway tickets. Assign them in Kitchen Display.
            </p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {unassigned.map((o) => (
                <OrderRow key={o.id} order={o} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TableCard({ table }: { table: BoardTable }) {
  const occupied = table.orders.length > 0;
  const hottest = table.orders.find((o) => o.status === 'Cooking')
    ?? table.orders.find((o) => o.status === 'Ready')
    ?? table.orders[0];
  return (
    <Card className={`flex flex-col gap-2 p-3 ${occupied ? 'ring-1 ring-orange-300' : ''}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[15px] font-semibold text-content-primary">{table.code}</div>
          {table.name && table.name !== table.code && (
            <div className="text-[11px] text-content-muted">{table.name}</div>
          )}
        </div>
        {occupied ? (
          <span
            className="rounded-full px-2 py-0.5 text-[11px] font-medium"
            style={{ background: statusColor(hottest!.status).bg, color: statusColor(hottest!.status).fg }}
          >
            {statusColor(hottest!.status).label}
          </span>
        ) : (
          <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700">
            Available
          </span>
        )}
      </div>

      {table.capacity != null && (
        <div className="flex items-center gap-1 text-[11px] text-content-muted">
          <Users size={11} /> Seats {table.capacity}
        </div>
      )}

      {table.orders.length > 0 && (
        <ul className="flex flex-col gap-1 border-t border-border pt-2 text-[12px]">
          {table.orders.map((o) => (
            <li key={o.id}>
              <OrderRow order={o} compact />
            </li>
          ))}
        </ul>
      )}

      <div className="mt-auto flex gap-2 border-t border-border pt-2">
        <Link
          to="/panel/restaurant/floor"
          className="flex-1 rounded-md border border-border px-2 py-1 text-center text-[11px] text-content-primary hover:bg-surface-card-hover"
        >
          Open in POS
        </Link>
      </div>
    </Card>
  );
}

function OrderRow({ order, compact }: { order: BoardOrder; compact?: boolean }) {
  const c = statusColor(order.status);
  const itemSummary = order.items?.slice(0, 3).map((i) => `${i.quantity}×${i.name}`).join(', ');
  return (
    <div className={`flex items-start gap-2 ${compact ? '' : 'rounded-md border border-border bg-surface-card p-2'}`}>
      <span
        className="mt-0.5 rounded-full px-2 py-0.5 text-[11px] font-medium"
        style={{ background: c.bg, color: c.fg }}
      >
        {c.label}
      </span>
      <div className="flex-1">
        <div className="text-[12px] font-medium text-content-primary">
          {order.code ?? `#${order.id}`}
          {order.customerName && <span className="ml-1 text-content-muted">· {order.customerName}</span>}
        </div>
        {itemSummary && (
          <div className="line-clamp-1 text-[11px] text-content-muted">{itemSummary}</div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */

function parseOrder(r: RestaurantOrder): BoardOrder {
  let parsed: {
    tableId?: string; tableCode?: string;
    customerName?: string;
    items?: Array<{ name: string; quantity: number }>;
    placedAt?: string;
  } = {};
  try {
    if (r.description && r.description.startsWith('{')) parsed = JSON.parse(r.description);
  } catch { /* leave empty */ }
  // Workflow stage is in string `orderStatus`; numeric `status` reads 0.
  const rawStage = (r as { orderStatus?: string | null }).orderStatus ?? r.status ?? '';
  const valid = (ORDER_STATUSES as readonly string[]).includes(rawStage)
    ? (rawStage as RestaurantOrderStatus)
    : 'Placed';
  return {
    id: String(r.id ?? ''),
    code: r.code ?? `#${r.id ?? ''}`,
    status: valid,
    customerName: parsed.customerName,
    items: parsed.items,
    placedAt: parsed.placedAt ?? r.indate,
    tableId: parsed.tableId,
  };
}

// keep import warning quiet
void CUSTOMER_VISIBLE_STEPS;
